import type { Rect } from '@flobi/artwork-core';
import type { Camera } from '@flobi/artwork-core/render';

export const MIN_ZOOM = 0.01;
export const MAX_ZOOM = 256;

const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
/** The least room, in CSS pixels, Fit keeps for the drawing before giving up chrome's space. */
const FIT_ROOM = 160;

/** Zooms by a factor while keeping the world point under (screenX, screenY) in place. */
export function zoomAt(camera: Camera, screenX: number, screenY: number, factor: number): Camera {
  const zoom = clamp(camera.zoom * factor, MIN_ZOOM, MAX_ZOOM);
  const worldX = camera.x + screenX / camera.zoom;
  const worldY = camera.y + screenY / camera.zoom;
  return { x: worldX - screenX / zoom, y: worldY - screenY / zoom, zoom };
}

export function panBy(camera: Camera, dxScreen: number, dyScreen: number): Camera {
  return { x: camera.x - dxScreen / camera.zoom, y: camera.y - dyScreen / camera.zoom, zoom: camera.zoom };
}

/** The camera that shows `bounds` centred in a view, with a margin in CSS pixels. */
/**
 * `inset` is the chrome the artwork must not hide behind.
 *
 * The canvas fills the window, but rails and drawers sit on top of the left and top of
 * it — nearly half the width once a drawer is out. Fitting to the whole window then
 * centres the page UNDER the drawer, which is the one place it must not be, and the
 * person's first move after every Fit is to drag the page back out into the open.
 */
export function fitCamera(
  bounds: Rect,
  viewWidth: number,
  viewHeight: number,
  margin = 48,
  inset: { top: number; left: number; right?: number; bottom?: number; reserve?: number } = { top: 0, left: 0 },
): Camera {
  // The side panel covers the trailing edge, and the tool dock the bottom one. `reserve`
  // is room kept free above what is fitted — the quick bar's band, so a chosen thing's bar
  // can sit above its artboard rather than jumping to the side of a page fitted too tight.
  //
  // In a window too short (or narrow) for all of that, the reserve goes first, then the
  // margins, then the chrome's own inset — before the zoom shrinks. Under about 300 px
  // the room left used to go negative and the drawing was fitted at 1%, a dot
  // (QA 2026-09-24, view-snapping-F13).
  let top = inset.top + (inset.reserve ?? 0);
  let bottom = inset.bottom ?? 0;
  if (viewHeight - top - bottom - 2 * margin < FIT_ROOM) top = inset.top;
  if (viewHeight - top - bottom < FIT_ROOM) {
    top = 0;
    bottom = 0;
  }
  let left = inset.left;
  let right = inset.right ?? 0;
  if (viewWidth - left - right < FIT_ROOM) {
    left = 0;
    right = 0;
  }
  const width = Math.max(1, viewWidth - left - right);
  const height = Math.max(1, viewHeight - top - bottom);
  const marginX = Math.max(0, Math.min(margin, (width - FIT_ROOM) / 2));
  const marginY = Math.max(0, Math.min(margin, (height - FIT_ROOM) / 2));
  const zoom = clamp(
    Math.min((width - 2 * marginX) / Math.max(bounds.width, 1), (height - 2 * marginY) / Math.max(bounds.height, 1)),
    MIN_ZOOM,
    MAX_ZOOM,
  );
  return {
    x: bounds.x + bounds.width / 2 - (left + width / 2) / zoom,
    y: bounds.y + bounds.height / 2 - (top + height / 2) / zoom,
    zoom,
  };
}
